import { moveItemInArray } from '@angular/cdk/drag-drop';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { OrderByPipe } from 'ngx-pipes';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';
import Swal from 'sweetalert2';

import { CardSubtaskService } from '@app/core/quotes/services/card-subtask.service';
import { StaffService } from '@app/core/staff';
import { IconSelectorComponent } from '@app/shared/components/icon-selector/icon-selector.component';

import { NdAttachmentPopupComponent } from '../nd-attachment-popup/nd-attachment-popup.component';

@Component({
  selector: 'cnc-nd-card-subtasks',
  templateUrl: './nd-card-subtasks.component.html',
  styleUrls: ['./nd-card-subtasks.component.scss'],
  providers: [OrderByPipe],
})
export class NdCardSubtasksComponent implements OnInit, OnDestroy {
  @Input() design;
  @Input() highlightSubtaskId;
  @Input() eventsOverwriteTemplate :Observable<void>;
  @Input() eventsSaveTemplate :Observable<void>;

  subtasks :any[] = [];
  isImporting :boolean;
  formData :any = {
    filesToUpload: [],
  };

  subscription :Subscription;
  eventsSaveTemplateSubscription :Subscription;
  eventsOverwriteTemplateSubscription :Subscription;

  templates :any = [];

  constructor(
    public staffSvc :StaffService,
    private cd :ChangeDetectorRef,
    private translate :TranslateService,
    private subtaskSvc :CardSubtaskService,
    private toastr :ToastrService,
    private orderBy :OrderByPipe,
    private modal :NgbModal,
  ) {}

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.eventsSaveTemplateSubscription.unsubscribe();
    this.eventsOverwriteTemplateSubscription.unsubscribe();
  }

  ngOnInit() {
    this.subscription = this.subtaskSvc
      .getDesignSubtasks(this.design.id)
      .subscribe((resp) => {
        this.subtasks = this.orderBy.transform(resp, ['order']);
        this.subtasks.forEach((row) => {
          row.assigned_user_ids = row.assigned_users.map((el) => el.user_id);
        });
      });

    this.subtaskSvc.getTemplates().subscribe((res) => {
      this.templates = res;
    });

    this.eventsSaveTemplateSubscription = this.eventsSaveTemplate.subscribe(() =>
      this.saveAsTemplate(),
    );
    this.eventsOverwriteTemplateSubscription =
      this.eventsOverwriteTemplate.subscribe(() => this.overwriteTemplate());
  }

  saveTask(task :any, isTemplate :boolean = false) {
    if (!task.text) return;

    this.isImporting = true;
    const fd = new FormData();
    fd.append('text', task ? task.text : '');
    fd.append('done', task ? task.done : '0');
    if (task.due_date) {
      fd.append('due_date', task.due_date);
    }
    fd.append('id', task && task.id ? task.id.toString() : '');
    fd.append(
      'assigned_users',
      task.assigned_user_ids ? JSON.stringify(task.assigned_user_ids) : '',
    );
    fd.append('icon', task && task.icon ? task.icon : '');
    fd.append('priority', task && task.priority ? task.priority : '');

    if (isTemplate) {
      fd.append('is_template', 'true');
      fd.append('template_name', task.template_name);
    }

    if (task && task.filesToUpload) {
      task.filesToUpload.forEach((file) => {
        fd.append('file[]', file.file);
      });
    }
    this.subtaskSvc.saveSubtask(this.design.id, fd).subscribe(
      (resp) => {
        this.clearTaskForm(task);
        this.isImporting = false;

        if (isTemplate) {
          this.toastr.success('Subtask template saved.');
          this.templates = [...this.templates, resp];
        } else {
          this.toastr.success('Subtask saved.');

          if (resp.assigned_users) {
            resp.assigned_user_ids = resp.assigned_users.map((row) => row.user_id);
          } else {
            resp.assigned_user_ids = [];
          }

          const idx = this.subtasks.findIndex((row) => row.id == resp.id);
          if (idx >= 0) {
            this.subtasks.splice(idx, 1, resp);
          } else {
            this.subtasks.splice(0, 0, resp);
            task = {};
            this.formData = {
              filesToUpload: [],
            };
          }
        }

        this.design.assigned_subtasks_count += 1;

        this.cd.detectChanges();
      },
      (err) => {
        this.toastr.error('Error saving task.');
        this.isImporting = false;
      },
    );
  }

  isDateDue(subtask :any) {
    if (subtask.done) {
      return false;
    }
    const today :Date = new Date();
    const dateDue :Date = new Date(subtask.due_date);

    return today >= dateDue;
  }

  drop(event) {
    // console.log(event);
    moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    const sortedIds :any[] = [];
    this.subtasks.forEach((row) => {
      sortedIds.push(row.id);
    });
    this.subtaskSvc
      .updateSubtaskOrders(this.design.id, { subtaskIds: sortedIds })
      .subscribe((resp) => {});
  }

  toggleCheckbox(subtask, idx) {
    // this.cancelSubtaskItem();
    const data = {
      done: subtask.done ? 1 : 0,
      id: subtask.id,
    };
    if (data.id != null) {
      this.subtaskSvc
        .toggleCheckSubtask(this.design.id, subtask.id, data)
        .subscribe(
          (resp) => {
            this.cd.detectChanges();
          },
          (err) => {},
        );
    }
  }

  handleSubtaskFiles(event :any, subTask ?:any) {
    const files = Array.prototype.slice.call(event.target.files);
    let maxSizeError = false;

    files.forEach((file, idx) => {
      if (file.size > 26214400) {
        maxSizeError = true;
      } else {
        const ftu :any = {
          previewSrc: '',
          file: file,
        };
        subTask.filesToUpload.push(ftu);
      }
    });

    if (maxSizeError) {
      Swal.fire({
        title: this.translate.instant('MESSAGES.FILE_MAX_SIZE_ERROR.TITLE'),
        text: this.translate.instant('MESSAGES.FILE_MAX_SIZE_ERROR.TEXT'),
        type: 'error',
        confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
      });
    }
    event.target.value = '';
  }

  clearTaskForm(subTask ?:any) {
    if (!subTask || !subTask.id) {
      subTask.filesToUpload = [];
      subTask.assigned_user_ids = [];
      subTask.text = '';
      subTask.icon = '';
      subTask.priority = '';
      subTask.due_date = '';
    }
    subTask.editable = false;
  }

  viewAttachment(attachment) {
    const data :any = {
      url: attachment.url,
      type: attachment.filetype,
      name: attachment.filename,
    };
    const modalRef = this.modal.open(NdAttachmentPopupComponent, {
      windowClass: 'myCustomModalClass',
    });
    modalRef.componentInstance.attachment = data;
  }

  deleteAttachment(subTask, attachmentId :number, idx :number) {
    Swal.fire({
      title: 'Confirm delete?',
      text: 'Delete attachment ?',
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: this.translate.instant('BUTTONS.CONFIRM_DELETE_TEXT'),
    }).then((result) => {
      if (result.value) {
        this.subtaskSvc.deleteAttachment(subTask.id, attachmentId).subscribe(
          (resp) => {
            subTask.assigned_attachments.splice(idx, 1);
            this.toastr.success('Successfully deleted attachment.', 'Success');
            this.cd.detectChanges();
          },
          (err) => {},
        );
      }
    });
  }

  deleteSubtask(subTask, idx) {
    Swal.fire({
      title: 'Confirm delete?',
      text: 'Delete subTask ?',
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: this.translate.instant('BUTTONS.CONFIRM_DELETE_TEXT'),
    }).then((result) => {
      if (result.value) {
        this.subtaskSvc.deleteSubtask(this.design.id, subTask.id).subscribe(
          (resp) => {
            this.toastr.success('Successfully deleted subtask.', 'Success');
            this.subtasks.splice(idx, 1);
            this.design.assigned_subtasks_count -= 1;
            this.cd.detectChanges();
          },
          (err) => {
            this.toastr.error('Error deleting subtask.');
          },
        );
      }
    });
  }

  getDescriptionLength(string :any) {
    return (
      string
        ? string
            .replace(/<.*?>/g, '') // replace html tags
            .replace(/&.*?;/g, '') // replace symbols
        : ''
    ).length;
  }

  getIcon(task :any) {
    const modalRef = this.modal.open(IconSelectorComponent, { size: 'lg' });
    modalRef.result.then((icon :any) => {
      if (icon && icon.ligature) {
        if (task.id) {
          const index = this.subtasks.findIndex((e) => e.id === task.id);
          this.subtasks[index] = {
            ...this.subtasks[index],
            icon: icon.ligature,
          };

          return;
        } else {
          this.formData.icon = icon.ligature;
        }

        this.cd.detectChanges();
      }
    });
  }

  sortByPriority(value) {
    console.log(value);
    // value = 0 - high, 1 - low
    if (parseInt(value)) {
      this.subtasks = this.subtasks.sort((a, b) => {
        const x = a.priority ? a.priority : 0;
        const y = b.priority ? b.priority : 0;

        return x < y ? 1 : -1;
      }); // descending
    } else {
      this.subtasks = this.subtasks.sort((a, b) => {
        const x = a.priority ? a.priority : 0;
        const y = b.priority ? b.priority : 0;

        return x > y ? 1 : -1;
      }); // ascending
    }

    this.cd.detectChanges();
  }

  async onChangeTemplateFn(template :any) {
    if (
      !template ||
      (this.design.subtask_template &&
        this.design.subtask_template.id === template.id)
    )
      return;

    const { value: accept } = await Swal.fire({
      title: 'Load from template',
      html: `
				<span class="text-warning">
					By default, items from template will be added to the existing subtasks. 
					If you wish to overwrite, please tick the checkbox below:
				</span>`,
      input: 'checkbox',
      inputValue: '',
      inputPlaceholder: 'Overwrite existing subtask items',
      confirmButtonText: 'Continue <i class="fa fa-arrow-right"></i>',
      showCancelButton: true,
    });

    if (accept === undefined) return;

    this.design.subtask_template = template;

    if (accept) {
      this.subtasks = [
        ...template.items.map((item) => {
          return {
            text: item.text,
            icon: item.icon,
            due_date: item.due_date,
            assigned_user_ids: item.assigned_users.map((u) => u.user_id),
            user: item.user,
            priority: item.priority,
            done: item.done,
            assigned_users: item.assigned_users,
            user_id: item.user.id,
            assigned_attachments: item.assigned_attachments,
          };
        }),
      ];
    } else {
      this.subtasks = [
        ...this.subtasks,
        ...template.items.map((item) => {
          return {
            text: item.text,
            icon: item.icon,
            due_date: item.due_date,
            assigned_user_ids: item.assigned_users.map((u) => u.user_id),
            user: item.user,
            priority: item.priority,
            done: item.done,
            assigned_users: item.assigned_users,
            user_id: item.user.id,
            assigned_attachments: item.assigned_attachments,
          };
        }),
      ];
    }

    this.cd.detectChanges();

    this.subtaskSvc
      .bulkAddSubtasks(this.design.id, {
        items: this.subtasks,
        overwrite: accept,
        template_id: template.id,
      })
      .subscribe(
        (resp) => {
          this.toastr.success('Subtask updated.');
        },
        (err) => {
          this.toastr.error('Error updating subtask.');
          console.log(err);
        },
      );
  }

  async saveTemplate(task :any) {
    if (!task.text) return;

    const { value: text } = await Swal.fire({
      title: 'Template Name',
      input: 'text',
      inputAttributes: {
        autocapitalize: 'off',
      },
      showCancelButton: true,
      confirmButtonText: 'Submit',
      showLoaderOnConfirm: true,
      inputValidator: (value) => {
        if (!value) return 'Template name is required.';
        else return null;
      },
    });

    if (text) this.saveTask({ ...task, template_name: text }, true);
  }

  async saveAsTemplate() {
    if (!this.subtasks || this.subtasks.length === 0) return;

    const { value: text } = await Swal.fire({
      title: 'Template Name',
      input: 'text',
      inputAttributes: {
        autocapitalize: 'off',
      },
      showCancelButton: true,
      confirmButtonText: 'Submit',
      showLoaderOnConfirm: true,
      inputValidator: (value) => {
        if (!value) return 'Template name is required.';
        else return null;
      },
    });

    if (text) {
      this.subtaskSvc
        .saveAsTemplate({ template_name: text, items: this.subtasks })
        .subscribe(
          (resp) => {
            this.toastr.success('Template saved.');
            this.templates = [...this.templates, resp];
          },
          (err) => {
            this.toastr.error('Error saving template.');
            this.isImporting = false;
          },
        );
    }
  }

  async overwriteTemplate() {
    if (!this.design.subtask_template || !this.subtasks.length) return;

    const { value: accept } = await Swal.fire({
      title: `Are you sure to overwrite "${this.design.subtask_template.template_name}" template?`,
      inputValue: '',
      confirmButtonText: 'Overwrite',
      showCancelButton: true,
    });

    if (!accept) return;

    this.subtaskSvc
      .saveAsTemplate({
        template_id: this.design.subtask_template.id,
        items: this.subtasks,
        overwrite: true,
      })
      .subscribe(
        (resp) => {
          this.toastr.success('Template updated.');
        },
        (err) => {
          this.toastr.error('Error updating template.');
          console.log(err);
        },
      );
  }
}
